/**
 * The block builder's grip: a ⋮⋮ handle beside whichever movable block (see cdm/blockMove.ts) the
 * pointer is over. Drag it to move the block; click it — or long-press the block on touch — for
 * Move up / Move down / Duplicate / Delete, plus "Edit binding…" on a generated data-bound block
 * (blocks/bindingEdit.ts) and "Save as block…" (blocks/teamBlocks.ts) when the page handles it. Alt+Shift+ArrowUp/Down do the moves from the keyboard.
 *
 * Rendered inside the editor's scroll container (which must be position: relative), so the handle
 * scrolls with the page rather than tracking it.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import type { Editor } from '@tiptap/core'
import { NodeSelection, type EditorState } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { colors, fontSize, radius, shadow } from '../../styles/theme'
import {
  findMovableBlock, moveBlock, duplicateBlock, deleteBlock, startBlockDrag, endBlockDrag, type MovableBlock,
} from '../../cdm/blockMove'
import { findBindingTarget } from '../../blocks/bindingEdit'
import { hasBlockSetupHandler, requestBlockSetup } from '../../blocks/paletteInsert'
import { hasSaveBlockHandler, movableBlockRange, requestSaveBlock } from '../../blocks/teamBlocks'

const HANDLE_SIZE = 20
const HANDLE_GAP = 6
const LONG_PRESS_MS = 500
const LONG_PRESS_SLOP_PX = 10

/** The movable block under a viewport point. x is clamped into the editor so the margin counts. */
export function hoverBlock(view: EditorView, clientX: number, clientY: number): MovableBlock | null {
  const r = view.dom.getBoundingClientRect()
  if (clientY < r.top || clientY > r.bottom) return null
  const left = Math.min(Math.max(clientX, r.left + 2), r.right - 2)
  const hit = view.posAtCoords({ left, top: clientY })
  if (!hit) return null
  return findMovableBlock(view.state.doc, hit.pos, hit.inside)
}

export function blockActions(state: EditorState, block: MovableBlock) {
  return {
    up: moveBlock(state, undefined, -1, block),
    down: moveBlock(state, undefined, 1, block),
    duplicate: duplicateBlock(state, undefined, block),
    remove: deleteBlock(state, undefined, block),
  }
}

interface Props {
  editor: Editor | null
  containerRef: RefObject<HTMLElement | null>
}

interface Placed { block: MovableBlock; top: number; left: number }

export default function BlockDragHandle({ editor, containerRef }: Props) {
  const [placed, setPlaced] = useState<Placed | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const handleRef = useRef<HTMLDivElement>(null)
  const pressRef = useRef<{ timer: number; x: number; y: number } | null>(null)

  const place = useCallback((block: MovableBlock | null) => {
    const container = containerRef.current
    if (!editor || !container || !block || !editor.isEditable) { setPlaced(null); return }
    const dom = editor.view.nodeDOM(block.pos)
    if (!(dom instanceof HTMLElement)) { setPlaced(null); return }
    const c = container.getBoundingClientRect()
    const r = dom.getBoundingClientRect()
    setPlaced({
      block,
      top: r.top - c.top + container.scrollTop + Math.min(4, r.height / 4),
      left: r.left - c.left + container.scrollLeft - HANDLE_SIZE - HANDLE_GAP,
    })
  }, [editor, containerRef])

  // Pointer tracking over the whole scroll container, so the margin the handle sits in keeps it shown.
  useEffect(() => {
    const container = containerRef.current
    if (!editor || !container) return
    const onMove = (e: MouseEvent) => {
      if (menuOpen) return
      if (handleRef.current?.contains(e.target as Node)) return
      if (!editor.isEditable || editor.view.dragging) { if (!editor.view.dragging) setPlaced(null); return }
      place(hoverBlock(editor.view, e.clientX, e.clientY))
    }
    const onLeave = () => { if (!menuOpen) setPlaced(null) }
    container.addEventListener('mousemove', onMove)
    container.addEventListener('mouseleave', onLeave)
    return () => {
      container.removeEventListener('mousemove', onMove)
      container.removeEventListener('mouseleave', onLeave)
    }
  }, [editor, containerRef, place, menuOpen])

  // Typing hides the handle until the pointer moves again; an edit also invalidates its position.
  useEffect(() => {
    if (!editor) return
    const onTx = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (transaction.docChanged && !menuOpen) setPlaced(null)
    }
    editor.on('transaction', onTx)
    return () => { editor.off('transaction', onTx) }
  }, [editor, menuOpen])

  // Long-press on touch opens the menu instead of dragging, which a phone cannot do precisely.
  useEffect(() => {
    const container = containerRef.current
    if (!editor || !container) return
    const cancel = () => { if (pressRef.current) { window.clearTimeout(pressRef.current.timer); pressRef.current = null } }
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !editor.isEditable) return
      if (!editor.view.dom.contains(e.target as Node)) return
      const t = e.touches[0]
      cancel()
      const x = t.clientX, y = t.clientY
      pressRef.current = {
        x, y,
        timer: window.setTimeout(() => {
          pressRef.current = null
          const block = hoverBlock(editor.view, x, y)
          if (!block) return
          place(block)
          setMenuOpen(true)
        }, LONG_PRESS_MS),
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      const p = pressRef.current
      if (!p) return
      const t = e.touches[0]
      if (Math.hypot(t.clientX - p.x, t.clientY - p.y) > LONG_PRESS_SLOP_PX) cancel()
    }
    container.addEventListener('touchstart', onStart, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: true })
    container.addEventListener('touchend', cancel)
    container.addEventListener('touchcancel', cancel)
    return () => {
      cancel()
      container.removeEventListener('touchstart', onStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', cancel)
      container.removeEventListener('touchcancel', cancel)
    }
  }, [editor, containerRef, place])

  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: Event) => {
      if (!handleRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  if (!editor || !placed || !editor.isEditable) return null
  // The block may have shifted since it was placed; re-check it is still where we think.
  const live = editor.state.doc.nodeAt(placed.block.pos) === placed.block.node ? placed.block : null
  if (!live) return null
  const can = blockActions(editor.state, live)
  const binding = menuOpen && hasBlockSetupHandler(editor) ? findBindingTarget(editor.state.doc, live) : null

  function editBinding() {
    if (!binding) return
    setMenuOpen(false)
    setPlaced(null)
    requestBlockSetup(editor!, { kind: binding.current.kind, edit: binding })
  }

  const canSave = menuOpen && hasSaveBlockHandler(editor)

  function saveAsBlock() {
    setMenuOpen(false)
    setPlaced(null)
    requestSaveBlock(editor!, movableBlockRange(editor!.state.doc, live!))
  }

  function run(action: 'up' | 'down' | 'duplicate' | 'remove') {
    const view = editor!.view
    view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, live!.pos)))
    const block = { pos: live!.pos, node: view.state.doc.nodeAt(live!.pos)! }
    if (action === 'up') moveBlock(view.state, view.dispatch, -1, block)
    else if (action === 'down') moveBlock(view.state, view.dispatch, 1, block)
    else if (action === 'duplicate') duplicateBlock(view.state, view.dispatch, block)
    else deleteBlock(view.state, view.dispatch, block)
    view.focus()
    setMenuOpen(false)
    setPlaced(null)
  }

  const label = live.node.type.name === 'loop' ? 'loop' : live.node.type.name === 'conditional' ? 'conditional' : 'block'
  const item = (enabled: boolean): CSSProperties => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none',
    fontSize: fontSize.sm, color: enabled ? colors.text : colors.textMuted, cursor: enabled ? 'pointer' : 'default', whiteSpace: 'nowrap',
  })

  return (
    <div ref={handleRef} style={{ position: 'absolute', top: placed.top, left: Math.max(0, placed.left), zIndex: 5 }}>
      <button
        type="button"
        draggable
        aria-label={`Move ${label}. Drag to move, or open actions. Alt+Shift+Arrow Up or Down moves it from the keyboard.`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="Drag to move · click for actions"
        onMouseDown={e => e.stopPropagation()}
        onClick={() => setMenuOpen(o => !o)}
        onDragStart={e => {
          setMenuOpen(false)
          startBlockDrag(editor.view, live, e.dataTransfer)
          const dom = editor.view.nodeDOM(live.pos)
          if (dom instanceof HTMLElement) e.dataTransfer.setDragImage(dom, 0, 0)
        }}
        onDragEnd={() => { endBlockDrag(editor.view); setPlaced(null) }}
        style={{
          width: HANDLE_SIZE, height: HANDLE_SIZE, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${menuOpen ? colors.primary : colors.border}`, borderRadius: radius.sm, background: colors.surface,
          color: colors.textSecondary, fontSize: 12, lineHeight: 1, letterSpacing: '-2px', cursor: 'grab', boxShadow: shadow.xs,
        }}
      >
        ⋮⋮
      </button>
      {menuOpen && (
        <div
          role="menu"
          aria-label={`${label} actions`}
          style={{ position: 'absolute', top: HANDLE_SIZE + 4, left: 0, minWidth: 140, padding: '4px 0', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxShadow: shadow.lg }}
        >
          <button type="button" role="menuitem" disabled={!can.up} onClick={() => run('up')} style={item(can.up)}>Move up</button>
          <button type="button" role="menuitem" disabled={!can.down} onClick={() => run('down')} style={item(can.down)}>Move down</button>
          <button type="button" role="menuitem" disabled={!can.duplicate} onClick={() => run('duplicate')} style={item(can.duplicate)}>Duplicate</button>
          {binding && <button type="button" role="menuitem" onClick={editBinding} style={item(true)}>Edit binding…</button>}
          {canSave && <button type="button" role="menuitem" onClick={saveAsBlock} style={item(true)}>Save as block…</button>}
          <button type="button" role="menuitem" disabled={!can.remove} onClick={() => run('remove')} style={{ ...item(can.remove), color: can.remove ? colors.error : colors.textMuted }}>Delete</button>
        </div>
      )}
    </div>
  )
}
